// asistenciaCalculos.js — la cuenta del módulo Asistencia en funciones puras (sin pantalla, sin Firebase): clasificar cada
// día, el resumen de cada colaborador y los ayudantes de tiempo.
//
// 🔴 HAY DOS COPIAS IDÉNTICAS de este archivo (06/10/2026):
//   - src/components/asistencia/asistenciaCalculos.js          → la usan Reporte, KPIs, Hoy, Horas Extras, Mi Asistencia y
//     Evaluación del Desempeño, a través de asistenciaEngine.js (que lee los datos y llama a armarAsistencia)
//   - functions/asistenteSistema/potente/asistenciaCalculos.js → la usa Lumi Potente (asistencia_hoy, asistencia_periodo)
// Así la asistencia que da Lumi es, por construcción, la de las pantallas. Se edita una y se copia la otra tal cual: las
// pruebas de los dos lados (asistenciaCalculos.test.js) frenan el deploy si las copias difieren. ARCHIVO SENSIBLE (tests de
// la matemática, CLAUDE.md): después de tocarlo, `npm run test:once`.
// 🔴 CommonJS a propósito (lo cargan el navegador y Node), sin `{ ...x }`, `[...x]`, `?.`, `??` ni desestructurar: en el
// build de producción Babel le agregaría un `import` de su ayuda y webpack dejaría de ver los exports (pasó con pruebaAB.js
// el 21/09/2026). La prueba del módulo lo compila con la receta del build y frena si aparece un `import`.
// Es el código que tenía asistenciaEngine.js hasta el 06/10/2026, movido sin cambiar ni una cuenta (la prueba lo compara con
// la copia vieja). Las reglas de cada categoría: ASISTENCIA_PLAN.md (anexo "Estado implementado consolidado").

'use strict'; // eslint-disable-line strict

function pad(n) { return String(n).padStart(2, '0'); }
function isoDe(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
function ddmmyyyy(iso) { var p = (iso || '').split('-'); return p.length === 3 ? p[2] + '/' + p[1] + '/' + p[0] : iso; }
function parseISO(iso) { var p = iso.split('-').map(Number); return new Date(p[0], p[1] - 1, p[2]); }
function dow(iso) { return ((parseISO(iso).getDay() + 6) % 7) + 1; } // 1=Lun..7=Dom
function minutos(hhmm) { if (!hhmm) return null; var p = hhmm.split(':').map(Number); return p[0] * 60 + p[1]; }
var DIAS_NOMBRE = { 1: 'Lun', 2: 'Mar', 3: 'Mié', 4: 'Jue', 5: 'Vie', 6: 'Sáb', 7: 'Dom' };
function hora12(hhmm) {
  if (!hhmm) return '';
  var p = hhmm.split(':').map(Number);
  return ((p[0] % 12) || 12) + ':' + pad(p[1]) + ' ' + (p[0] < 12 ? 'a. m.' : 'p. m.');
}
function fmtMin(min) {
  var v = Math.round(min || 0);
  if (v <= 0) return '0 min';
  var h = Math.floor(v / 60), m = v % 60;
  if (h === 0) return m + ' min';
  if (m === 0) return h + ' h';
  return h + ' h ' + m + ' min';
}
// Duración larga: pasa a días cuando supera 24h. Ej. 1542 -> "1 d 1 h 42 min".
function fmtDur(min) {
  var v = Math.round(min || 0);
  if (v <= 0) return '0 min';
  var d = Math.floor(v / 1440), h = Math.floor((v % 1440) / 60), m = v % 60;
  var parts = [];
  if (d) parts.push(d + ' d');
  if (h) parts.push(h + ' h');
  if (m || parts.length === 0) parts.push(m + ' min');
  return parts.join(' ');
}
function rangoDias(desde, hasta) {
  var out = []; var d = parseISO(desde); var fin = parseISO(hasta); var g = 0;
  while (d <= fin && g < 1000) { out.push(isoDe(d)); d.setDate(d.getDate() + 1); g++; }
  return out;
}

// Horario efectivo para una fecha: el base + la excepción del día si existe
// (horario.porDia = { '5': { salida: '16:00', ... } } → los viernes salen distinto).
function horarioDelDia(horario, fecha) {
  var ov = horario && horario.porDia && horario.porDia[String(dow(fecha))];
  return ov ? Object.assign({}, horario, ov) : horario;
}

// Horario VIGENTE de un colaborador para una fecha (ago 2026).
//
// El horario de una persona cambia con los meses, y la política es que el mes se
// trabaja completo con el horario con el que arrancó. Por eso el expediente guarda
// `datosLaborales.horarios`: una LISTA de versiones, cada una con el mes desde el
// que rige (`desde: 'YYYY-MM'`, siempre el día 1). Rige la última versión cuyo mes
// sea menor o igual al de la fecha, y sigue rigiendo hasta que aparezca otra.
//
// 🔴 Una versión SIN `desde` rige "desde el inicio". Así quedan los horarios que ya
// existían antes de versionar: mientras nadie cargue una versión con fecha, el
// cálculo da EXACTAMENTE lo mismo que antes y ningún reporte viejo se mueve.
// Si no hay lista, se devuelve `horarioBase` (el de siempre) tal cual.
// Desde el 23/10/2026 una versión también puede arrancar un DÍA (`desde: 'YYYY-MM-DD'`, Toto: "que empieza x día"):
// se compara contra la fecha completa. Y si hay versiones válidas pero ninguna rige todavía en esa fecha, el día NO
// tiene horario (null): antes caía al horario suelto, que es el espejo de HOY, y un horario que arranca a mitad de mes
// habría medido los días anteriores con el nuevo. Al 23/10/2026 ningún expediente estaba en ese caso (las 4 listas que
// había tenían su versión "desde el inicio"), así que ningún reporte se mueve.
function horarioVigente(horarios, horarioBase, fecha) {
  if (!Array.isArray(horarios) || horarios.length === 0) return horarioBase || null;
  var mes = String(fecha || '').slice(0, 7);
  var dia = String(fecha || '').slice(0, 10);
  var elegido = null;
  var hayValidas = false;
  for (var i = 0; i < horarios.length; i++) {
    var h = horarios[i];
    if (!esHorarioValido(h)) continue;
    hayValidas = true;
    var desde = h.desde || '';            // '' = desde el inicio (gana contra cualquiera)
    if (desde && desde > (desde.length > 7 ? dia : mes)) continue;   // esta versión todavía no arranca
    if (!elegido || (elegido.desde || '') <= desde) elegido = h;
  }
  if (hayValidas) return elegido;
  return horarioBase || null;
}

// ¿Es un horario que se puede medir? Uno con hora de entrada (FIJO) o uno "sin hora fija" (JORNADA).
function esHorarioValido(h) { return !!h && (!!h.entrada || h.tipo === 'JORNADA'); }

// "Sin hora fija" (23/10/2026, los mensajeros): marca el INICIO y el FIN de su jornada desde el celular, a cualquier
// hora y desde donde esté. Se le miden los días y la hora de inicio y de fin, nunca tardanzas. La versión del horario se
// guarda como { tipo: 'JORNADA', diasLaborales, desde } sin entrada ni salida. docs/planes/ASISTENCIA_JORNADA_SIN_HORA_FIJA_PLAN.md
function esSinHoraFija(h) { return !!h && h.tipo === 'JORNADA'; }

// Horas a descontar por un PERMISO POR HORAS dentro de la jornada del día
// (respetando excepciones porDia). El permiso es un RANGO desde→hasta:
// - sin horaFin (permisos viejos y "salir temprano") → hasta = su salida del día
// - "llegar tarde" → desde su entrada hasta horaFin
// - bloque intermedio (ej. cita médica) → desde→hasta dentro de la jornada
// Resta la franja de almuerzo si cae dentro del permiso (decisión de Toto: esa hora
// no es laboral, no debe pagarla con vacaciones) y redondea a la MEDIA HORA más
// cercana (mínimo 0.5). Devuelve { minutosNetos, horas } o null si no es calculable.
function calcularHorasPermiso(horarioBase, fecha, horaInicio, horaFin) {
  if (!horarioBase || !horarioBase.salida || !fecha || !horaInicio) return null;
  var h = horarioDelDia(horarioBase, fecha);
  var ini = minutos(horaInicio);
  var fin = horaFin ? minutos(horaFin) : minutos(h.salida);
  var ent = minutos(h.entrada);
  var sal = minutos(h.salida);
  if (ini == null || fin == null || ini >= fin) return null;
  // El rango debe caer DENTRO de su jornada (no antes de su entrada ni después de
  // su salida): sin esto, "salgo a la 1:00 a. m." calculaba 16 horas.
  if (ent != null && ini < ent) return null;
  if (sal != null && fin > sal) return null;
  var neto = fin - ini;
  if (h.aplicaAlmuerzo !== false) {
    var aS = minutos(h.almuerzoSalida);
    var aR = minutos(h.almuerzoRegreso);
    if (aS != null && aR != null && aR > aS) neto -= Math.max(0, Math.min(fin, aR) - Math.max(ini, aS));
  }
  if (neto <= 0) return null;
  var horas = Math.max(0.5, Math.round((neto / 60) * 2) / 2);
  return { minutosNetos: neto, horas: horas };
}

// Un día laborable de alguien SIN HORA FIJA (tipo 'JORNADA'): inicio = su primera ENTRADA, fin = su última SALIDA
// después del inicio (las marcas de almuerzo, si marcó en el kiosko, no cuentan). Nunca hay tardanza, salida temprana,
// almuerzo ni horas extra. 🔴 De inicio a fin NO son horas trabajadas (Toto, 23/10/2026: "a veces descansan, comen…
// podrían marcar hasta las 9 p. m. para que pensemos que trabajaron mucho tiempo"): la duración va aparte, en
// `duracionMin`, y `trabajadoMin` queda vacío para que no sume a "Horas laboradas". Sin fin → `jornadaAbierta`: el día
// cuenta como trabajado y RRHH completa el fin en Corrección.
function clasificarJornada(marcasDia) {
  var marks = (marcasDia || []).slice().sort(function (a, b) {
    var ma = minutos(a.hora), mb = minutos(b.hora);
    return ((ma != null ? ma : 99999) - (mb != null ? mb : 99999)) || ((a.creadoEnMs || 0) - (b.creadoEnMs || 0));
  });
  var inicio = marks.find(function (m) { return m.tipo === 'ENTRADA'; });
  if (!inicio) return { cat: 'AUSENTE' };
  var mi = minutos(inicio.hora);
  var fin = marks.slice().reverse().find(function (m) {
    var mf = minutos(m.hora);
    return m.tipo === 'SALIDA' && mi != null && mf != null && mf >= mi;
  });
  var duracionMin = fin ? (minutos(fin.hora) - mi) : null;
  return {
    cat: 'JORNADA',
    entrada: inicio.hora, salida: fin ? fin.hora : null,
    almSalida: null, almRegreso: null,
    tardanzaMin: 0, salidaTempranaMin: 0, almuerzoExcesoMin: 0,
    estadoAlmuerzo: null, almuerzoFueraHorario: false, almuerzoTomado: false,
    estadoSalida: null,
    trabajadoMin: null, extraMin: 0, permisoDesde: null, permisoHasta: null,
    duracionMin: duracionMin, jornadaAbierta: !fin,
    salidaFotoUrl: fin ? (fin.fotoUrl || '') : ''
  };
}

function clasificar(fecha, horarioBase, marcasDia, esFeriado, esVacacion, esPermiso, esIncapacidad, esOmitido, permisoRango, esHomeOffice) {
  var dias = Array.isArray(horarioBase.diasLaborales) && horarioBase.diasLaborales.length ? horarioBase.diasLaborales : [1, 2, 3, 4, 5];
  if (!dias.includes(dow(fecha))) return { cat: 'DESCANSO' };
  if (esFeriado) return { cat: 'FERIADO' };
  // Día omitido (ej. período previo a que existiera el control): cuenta como justificado
  if (esOmitido) return { cat: 'OMITIDO' };
  // Home office aprobado: jornada completa trabajada desde casa, SIN marcar. No es
  // ausencia ni descanso; se acredita el tiempo esperado del horario de ese día como
  // laborado (sin tardanza). Gana sobre vacaciones/permiso/incapacidad porque es una
  // autorización explícita de "ese día sí trabaja, pero desde su casa".
  if (esHomeOffice) {
    var hHO = horarioDelDia(horarioBase, fecha);
    var entHO = minutos(hHO.entrada), salHO = minutos(hHO.salida);
    var jornadaHO = (entHO != null && salHO != null && salHO > entHO) ? (salHO - entHO) : 0;
    if (hHO.aplicaAlmuerzo !== false) {
      var aSHO = minutos(hHO.almuerzoSalida), aRHO = minutos(hHO.almuerzoRegreso);
      if (aSHO != null && aRHO != null && aRHO > aSHO) jornadaHO -= (aRHO - aSHO);
    }
    return { cat: 'HOME_OFFICE', trabajadoMin: Math.max(0, jornadaHO) };
  }
  if (esVacacion) return { cat: 'VACACIONES' };
  if (esPermiso) return { cat: 'PERMISO' };
  if (esIncapacidad) return { cat: 'INCAPACIDAD' };
  // Sin hora fija: su propia cuenta (abajo). Los permisos por horas no le aplican: no hay jornada contra la cual medirlos.
  if (esSinHoraFija(horarioBase)) return clasificarJornada(marcasDia);
  var horario = horarioDelDia(horarioBase, fecha); // aplica la excepción del día (si hay)

  // PERMISO POR HORAS aprobado: un RANGO desde→hasta dentro de la jornada
  // ({desde, hasta} en minutos; hasta null = su salida; acepta número viejo = desde).
  // Casos: cubre el FINAL (sale temprano: la jornada termina ahí), cubre el INICIO
  // (llega tarde con permiso: la jornada empieza al terminar el permiso), bloque
  // INTERMEDIO (cita médica: se descuenta del trabajado), o TODA la jornada (día
  // completo PERMISO, sin esperar marca). Si el permiso tapa la franja de almuerzo,
  // el almuerzo no se exige ni penaliza.
  var permisoDesde = null, permisoHasta = null, permisoIntermedios = [];
  if (permisoRango != null) {
    // Acepta: número viejo (= solo "desde"), un objeto {desde, hasta}, o un ARRAY de
    // varios permisos del mismo día (ej. uno que pidió el colaborador + otro cargado
    // manual por RRHH). Los que se solapan o se encadenan se FUSIONAN en un solo bloque
    // continuo (8-9 + 9-10:10 = 8-10:10), así un permiso partido en varios pedidos
    // cuenta como uno solo.
    var entBase = minutos(horario.entrada);
    var salBase = minutos(horario.salida);
    var hhmm = function (m) { return pad(Math.floor(m / 60)) + ':' + pad(m % 60); };
    var crudos = (typeof permisoRango === 'number')
      ? [{ desde: permisoRango, hasta: null }]
      : (Array.isArray(permisoRango) ? permisoRango : [permisoRango]);
    // Resolver "hasta" null = su salida, validar y ordenar por inicio.
    var rangos = crudos
      .map(function (r) { return { pd: r.desde, ph: (r.hasta != null) ? r.hasta : salBase }; })
      .filter(function (r) { return r.pd != null && r.ph != null && r.ph > r.pd; })
      .sort(function (a, b) { return a.pd - b.pd; });
    // Fusionar solapados/contiguos.
    var merged = [];
    rangos.forEach(function (r) {
      var last = merged[merged.length - 1];
      if (last && r.pd <= last.ph) last.ph = Math.max(last.ph, r.ph);
      else merged.push(Object.assign({}, r));
    });
    if (merged.length) {
      var almSalidaHorario = minutos(horario.almuerzoSalida);
      var almRegresoHorario = minutos(horario.almuerzoRegreso);
      // Cubre TODA la jornada (un solo bloque continuo de entrada a salida): día PERMISO.
      if (merged.length === 1 && entBase != null && salBase != null &&
          merged[0].pd <= entBase && merged[0].ph >= salBase) {
        return { cat: 'PERMISO' };
      }
      merged.forEach(function (bloque) {
        var pd = bloque.pd, ph = bloque.ph;
        var tapaAlmuerzo = (almSalidaHorario != null && almRegresoHorario != null && almRegresoHorario > almSalidaHorario && pd < almRegresoHorario && ph > almSalidaHorario);
        if (salBase != null && ph >= salBase && pd < salBase) {
          // Cubre el FINAL: salir a la hora del permiso = a tiempo (tolerancia normal)
          permisoDesde = hhmm(pd);
          horario = Object.assign({}, horario, { salida: permisoDesde });
          if (tapaAlmuerzo || (almRegresoHorario != null && pd <= almRegresoHorario)) horario = Object.assign({}, horario, { aplicaAlmuerzo: false });
        } else if (entBase != null && pd <= entBase && ph > entBase) {
          // Cubre el INICIO: entrar a la hora en que termina el permiso = a tiempo
          permisoHasta = hhmm(ph);
          horario = Object.assign({}, horario, { entrada: permisoHasta });
          if (tapaAlmuerzo) horario = Object.assign({}, horario, { aplicaAlmuerzo: false });
        } else {
          // Bloque INTERMEDIO: los extremos de la jornada no cambian; el bloque se
          // descuenta del tiempo laborado (y absorbe el almuerzo si lo tapa)
          permisoDesde = permisoDesde || hhmm(pd);
          permisoHasta = permisoHasta || hhmm(ph);
          permisoIntermedios.push({ pd: pd, ph: ph });
          if (tapaAlmuerzo) horario = Object.assign({}, horario, { aplicaAlmuerzo: false });
        }
      });
    }
  }

  var marks = (marcasDia || []).slice().sort(function (a, b) {
    var ma = minutos(a.hora), mb = minutos(b.hora);
    return ((ma != null ? ma : 99999) - (mb != null ? mb : 99999)) || ((a.creadoEnMs || 0) - (b.creadoEnMs || 0));
  });
  var entrada = marks.find(function (m) { return m.tipo === 'ENTRADA'; });
  if (!entrada) return { cat: 'AUSENTE' };
  var salida = marks.slice().reverse().find(function (m) { return m.tipo === 'SALIDA'; });
  var almS = marks.find(function (m) { return m.tipo === 'SALIDA_ALMUERZO'; });
  var almR = marks.find(function (m) { return m.tipo === 'REGRESO_ALMUERZO'; });

  // Tolerancia = período de gracia, NO descuento: si entra dentro de la gracia es
  // "a tiempo" (0 min); si la pasa, la tardanza cuenta COMPLETA desde su hora de
  // entrada (ej. entrada 8:00, gracia 10, llega 8:26 → 26 min tarde, no 16).
  var me = minutos(entrada.hora), mh = minutos(horario.entrada);
  var tolE = Number(horario.toleranciaMin); var tolEntrada = Number.isFinite(tolE) ? tolE : 10;
  var tardanzaMin = 0;
  if (me != null && mh != null && me > mh + tolEntrada) tardanzaMin = me - mh;

  // Igual que la entrada: si sale dentro de la tolerancia es "a tiempo" (0 min);
  // si sale antes de eso, se cuenta COMPLETO desde su hora de salida.
  var ms = salida ? minutos(salida.hora) : null;
  var mhSal = minutos(horario.salida);
  var tolS = Number(horario.toleranciaSalidaMin); var tolSalida = Number.isFinite(tolS) ? tolS : 10;
  var salidaTempranaMin = 0;
  if (ms != null && mhSal != null && ms < mhSal - tolSalida) salidaTempranaMin = mhSal - ms;

  var aplicaAlm = horario.aplicaAlmuerzo !== false;
  var aS = almS ? minutos(almS.hora) : null;
  var aR = almR ? minutos(almR.hora) : null;
  var asignadoS = minutos(horario.almuerzoSalida);
  var asignadoR = minutos(horario.almuerzoRegreso);
  var almuerzoAsignado = (asignadoS != null && asignadoR != null && asignadoR > asignadoS) ? (asignadoR - asignadoS) : null;
  // Todo almuerzo MARCADO cuenta, aunque haya durado 0 min (salida y regreso en el
  // mismo minuto): antes esos quedaban sin clasificar y los totales no cuadraban.
  var almuerzoTomado = (aS != null && aR != null);
  var almuerzoReal = (almuerzoTomado && aR >= aS) ? (aR - aS) : 0;
  // El exceso de almuerzo cuenta COMPLETO (sin tolerancia): la tolerancia de
  // almuerzo es SOLO para la bandera "fuera de horario" de abajo.
  var almuerzoExcesoMin = 0; var estadoAlmuerzo = null;
  if (almuerzoTomado && almuerzoAsignado != null) {
    almuerzoExcesoMin = Math.max(0, almuerzoReal - almuerzoAsignado);
    estadoAlmuerzo = almuerzoExcesoMin > 0 ? 'EXCEDIDO' : 'OK';
  }
  // Almuerzo fuera de su franja (tolerancia propia del expediente, default 15)
  var tolAlm = Number(horario.toleranciaAlmuerzoMin); var tolAlmuerzo = Number.isFinite(tolAlm) ? tolAlm : 15;
  var almuerzoFueraHorario = (aS != null && asignadoS != null) ? (Math.abs(aS - asignadoS) > tolAlmuerzo) : false;

  // Tiempo laborado = tiempo DENTRO del horario (gracia no suma, salir después no
  // suma —va a extras—), menos el almuerzo (al menos el asignado; si se pasó, el real).
  var trabajadoMin = null;
  var extraMin = 0; // horas extra crudas: tiempo después de la salida del horario
  if (me != null && ms != null && ms > me) {
    var ini = (mh != null) ? Math.max(me, mh) : me;
    var fin = (mhSal != null) ? Math.min(ms, mhSal) : ms;
    var tot = fin - ini;
    if (aplicaAlm) tot -= (almuerzoAsignado != null ? Math.max(almuerzoReal, almuerzoAsignado) : almuerzoReal);
    // Permiso de bloque intermedio: esos rangos no son laborados
    permisoIntermedios.forEach(function (pi) { tot -= Math.max(0, Math.min(fin, pi.ph) - Math.max(ini, pi.pd)); });
    trabajadoMin = tot > 0 ? tot : 0;
    if (mhSal != null && ms > mhSal) extraMin = ms - mhSal;
  }

  return {
    cat: tardanzaMin > 0 ? 'TARDE' : 'A_TIEMPO',
    entrada: entrada.hora, salida: salida ? salida.hora : null,
    almSalida: almS ? almS.hora : null, almRegreso: almR ? almR.hora : null,
    tardanzaMin: tardanzaMin, salidaTempranaMin: salidaTempranaMin, almuerzoExcesoMin: almuerzoExcesoMin,
    estadoAlmuerzo: estadoAlmuerzo, almuerzoFueraHorario: almuerzoFueraHorario, almuerzoTomado: almuerzoTomado,
    estadoSalida: salida ? (salidaTempranaMin > 0 ? 'TEMPRANO' : 'A_TIEMPO') : null,
    trabajadoMin: trabajadoMin, extraMin: extraMin, permisoDesde: permisoDesde, permisoHasta: permisoHasta,
    salidaFotoUrl: salida ? (salida.fotoUrl || '') : ''
  };
}

/**
 * Los colaboradores que entran en el análisis: con horario en su expediente, usuario activo con correo, fuera de las
 * exclusiones (`asistencia_config/exclusiones`) y, si se pide, de un departamento o un solo correo.
 * `o`: { expedientes: [{ id, data }], usuariosById: { id: { email, nombre, activo, departamentoEmpresaId } },
 *        exclusiones: { departamentos, usuarios } | null, soloEmail, filtroDepto }
 */
function colaboradoresConHorario(o) {
  var soloEmail = (o.soloEmail || '').toLowerCase();
  // Exclusiones configuradas (socios/gerentes, etc.). Como antes del 06/10/2026, un dato raro en el documento no corta
  // el cálculo: se queda con lo que alcanzó a leer (los `try` de abajo hacen lo mismo con cada colección).
  var exDept = []; var exUsr = [];
  try {
    if (o.exclusiones) {
      exDept = o.exclusiones.departamentos || [];
      exUsr = (o.exclusiones.usuarios || []).map(function (e) { return (e || '').toLowerCase(); });
    }
  } catch (e) { /* noop */ }
  var usuariosById = o.usuariosById || {};
  var tracked = [];
  (o.expedientes || []).forEach(function (d) {
    var data = d.data;
    var dl = (data && data.datosLaborales) || null;
    var horario = dl ? dl.horario : undefined;
    // Versiones del horario por mes (ver horarioVigente). El horario suelto de
    // siempre se sigue leyendo: es el que rige mientras no haya lista.
    // Válidas = con hora de entrada o "sin hora fija" (tipo JORNADA, 23/10/2026).
    var horariosLista = (dl && Array.isArray(dl.horarios))
      ? dl.horarios.filter(esHorarioValido)
      : null;
    var tieneHorario = esHorarioValido(horario) || (horariosLista && horariosLista.length);
    if (!tieneHorario) return;
    var u = usuariosById[d.id];
    if (!u || !u.email || u.activo === false) return;
    if (soloEmail && (u.email || '').toLowerCase() !== soloEmail) return;
    if (exUsr.includes((u.email || '').toLowerCase())) return;
    var deptoId = (dl && dl.departamentoEmpresaId) || u.departamentoEmpresaId || '';
    if (exDept.includes(deptoId)) return;
    if (o.filtroDepto && deptoId !== o.filtroDepto) return;
    // Fecha de inicio de labores (YYYY-MM-DD, del expediente): los días ANTERIORES
    // no se clasifican como AUSENTE — la persona todavía no trabajaba acá.
    var inicioLabores = (dl && dl.fechaInicioLabores) || null;
    tracked.push({ usuarioId: d.id, email: u.email, nombre: u.nombre || u.email, deptoId: deptoId, horario: horario, horarios: horariosLista, inicioLabores: inicioLabores });
  });
  return tracked;
}

/**
 * El cálculo de computeAsistencia con los datos YA LEÍDOS (asistenciaEngine.js los lee con el SDK del navegador; Lumi,
 * con el de administrador, con las mismas consultas). Devuelve { filas, diasRango, trackedCount }, con
 * filas = [{ usuarioId, email, nombre, deptoId, horario, horarios, inicioLabores, deptoNom, resumen, detalleDias }].
 * `o`: { desde, hasta, tracked (de colaboradoresConHorario), deptoNombre: { id: nombre }, datos: {
 *   marcas (asistencia_marcas del rango), feriadosPorAnio: { 'AAAA': doc | null }, solicitudesVacaciones (fechaFin >= desde),
 *   registrosVacaciones (todos), incapacidades, homeOffice, omisiones, horariosEspeciales (fechaFin >= desde) } }
 */
function armarAsistencia(o) {
  var desde = o.desde, hasta = o.hasta;
  var diasArr = rangoDias(desde, hasta);
  var tracked = o.tracked || [];
  var datos = o.datos || {};
  var deptoNombre = o.deptoNombre || {};

  var marcasPorEmailFecha = {};
  (datos.marcas || []).forEach(function (m) {
    if (m.anulada) return;
    var k = (m.usuarioEmail || '').toLowerCase() + '|' + m.fecha;
    (marcasPorEmailFecha[k] = marcasPorEmailFecha[k] || []).push(m);
  });

  var feriados = new Set();
  var feriadosEspPorEmail = {}; // feriados ESPECIALES: email(minúsculas) -> Set(fechas)
  var porAnio = datos.feriadosPorAnio || {};
  Object.keys(porAnio).forEach(function (a) {
    var fd = porAnio[a];
    if (!fd) return;
    if (Array.isArray(fd.feriados)) fd.feriados.forEach(function (f) { feriados.add(f.fecha); });
    if (Array.isArray(fd.especiales)) fd.especiales.forEach(function (e) {
      (e.usuarios || []).forEach(function (u) {
        var k = (u || '').toLowerCase();
        (feriadosEspPorEmail[k] = feriadosEspPorEmail[k] || new Set()).add(e.fecha);
      });
    });
  });

  var vacPorUid = {};
  try {
    (datos.solicitudesVacaciones || []).forEach(function (s) {
      if (!['autorizada', 'gozada'].includes(s.estado)) return;
      if (!s.fechaInicio || s.fechaInicio > hasta) return;
      (vacPorUid[s.usuarioId] = vacPorUid[s.usuarioId] || []).push([s.fechaInicio, s.fechaFin]);
    });
  } catch (e) { /* noop */ }

  var permPorUid = {};
  var permHorasPorUid = {}; // permisos POR HORAS aprobados: uid -> { fecha: [{desde, hasta}] en minutos (hasta null = su salida). Array: varios permisos el mismo día se fusionan en clasificar() }
  try { (datos.registrosVacaciones || []).forEach(function (r) {
    if (r.estado === 'anulado' || r.estado === 'anulada') return;
    var add = function (f) { (permPorUid[r.usuarioId] = permPorUid[r.usuarioId] || new Set()).add(f); };
    if (r.tipoRegistro === 'dia' && r.fecha) add(r.fecha);
    else if (r.tipoRegistro === 'periodo' && r.fechaInicio && r.fechaFin) rangoDias(r.fechaInicio, r.fechaFin).forEach(add);
    else if (r.tipoRegistro === 'horas' && r.fecha && r.horaInicio) {
      // Permiso por horas CON hora: la asistencia de ese día se mide contra el
      // RANGO del permiso. `horaFin` null = hasta su salida (permisos viejos y
      // "salir temprano"). (Registros viejos sin horaInicio no se pueden mapear
      // y siguen siendo solo un descuento de saldo.)
      var m = minutos(r.horaInicio);
      var mf = r.horaFin ? minutos(r.horaFin) : null;
      if (m != null) {
        var mapa = (permHorasPorUid[r.usuarioId] = permHorasPorUid[r.usuarioId] || {});
        // Se acumulan TODOS los permisos por horas del día; clasificar() los fusiona.
        (mapa[r.fecha] = mapa[r.fecha] || []).push({ desde: m, hasta: mf });
      }
    }
  }); } catch (e) { /* noop */ }

  // Una incapacidad ANULADA (IncapacidadesTab, 28/09/2026) ya no justifica esos días: se ignora igual que
  // una omisión anulada. Queda en la base como registro.
  var incPorEmail = {};
  try {
    (datos.incapacidades || []).forEach(function (x) {
      if (x.anulada) return;
      if (!x.fechaInicio || x.fechaInicio > hasta) return;
      var key = (x.usuarioEmail || '').toLowerCase();
      (incPorEmail[key] = incPorEmail[key] || []).push([x.fechaInicio, x.fechaFin]);
    });
  } catch (e) { /* noop */ }

  // Home office aprobado (asistencia_home_office): días que el colaborador trabajó
  // desde casa. Cruce por usuarioId (como vacaciones/permisos).
  var hoPorUid = {};
  try {
    (datos.homeOffice || []).forEach(function (x) {
      if (x.estado === 'anulado' || x.estado === 'anulada') return;
      if (!x.fechaInicio || x.fechaInicio > hasta || !x.fechaFin || x.fechaFin < desde) return;
      (hoPorUid[x.usuarioId] = hoPorUid[x.usuarioId] || []).push([x.fechaInicio, x.fechaFin]);
    });
  } catch (e) { /* noop */ }

  // Días omitidos (períodos sin control de asistencia, ej. antes de implementar el
  // módulo). usuarioEmail '*' = aplica a TODOS los colaboradores.
  var omiPorEmail = {};
  try {
    (datos.omisiones || []).forEach(function (x) {
      if (x.anulada) return;
      if (!x.fechaInicio || x.fechaInicio > hasta) return;
      var key = (x.usuarioEmail || '').toLowerCase();
      (omiPorEmail[key] = omiPorEmail[key] || []).push([x.fechaInicio, x.fechaFin]);
    });
  } catch (e) { /* noop */ }

  // Horarios ESPECIALES por fecha (ej. "hoy Producción entra y sale 1 hora antes"):
  // para esa(s) fecha(s) el horario del doc reemplaza al normal (gana también
  // sobre las excepciones porDia). usuarios ['*'] = todos los colaboradores.
  var horEspGlobal = [];   // [[fechaInicio, fechaFin, horario], ...]
  var horEspPorEmail = {}; // email -> [[fechaInicio, fechaFin, horario], ...]
  try {
    (datos.horariosEspeciales || []).forEach(function (x) {
      if (x.anulada) return;
      if (!x.fechaInicio || x.fechaInicio > hasta) return;
      if (!x.horario || !x.horario.entrada || !x.horario.salida) return;
      var item = [x.fechaInicio, x.fechaFin, x.horario];
      (x.usuarios || []).forEach(function (u) {
        var k = (u || '').toLowerCase();
        if (k === '*') horEspGlobal.push(item);
        else (horEspPorEmail[k] = horEspPorEmail[k] || []).push(item);
      });
    });
  } catch (e) { /* noop */ }
  // Horario especial vigente para una fecha (el específico del colaborador gana al global)
  var horEspDe = function (arr, f) {
    var lista = arr || [];
    for (var i = 0; i < lista.length; i++) { if (f >= lista[i][0] && f <= lista[i][1]) return lista[i][2]; }
    return null;
  };

  var enRango = function (rangosF, f) { return (rangosF || []).some(function (r) { return f >= r[0] && f <= r[1]; }); };

  // Marca pendiente de REVISIÓN: rostro no confirmado o bandera del móvil, sin revisar
  // (las anuladas ya quedaron fuera al agrupar). Se cuenta por día para avisar en el
  // Reporte sin lecturas extra (las marcas ya están cargadas).
  var pendientesDe = function (marcasF) {
    return (marcasF || []).filter(function (m) {
      return !m.revisada && ((m.match && (m.match.estado === 'DUDOSA' || m.match.estado === 'SIN_MATCH')) || m.requiereRevision);
    }).length;
  };

  var filas = tracked.map(function (emp) {
    var resumen = { diasLaborables: 0, presentes: 0, tardanzas: 0, tardanzaMin: 0, almuerzosExcedidos: 0, almuerzoExcesoMin: 0, salidasTempranas: 0, salidaTempranaMin: 0, ausentes: 0, justificadas: 0, feriados: 0, homeOffice: 0, trabajadoMin: 0, revisionPendientes: 0 };
    var detalleDias = [];
    var emailMin = emp.email.toLowerCase();
    var espEmp = feriadosEspPorEmail[emailMin]; // sus feriados especiales (si tiene)
    diasArr.forEach(function (f) {
      // Día anterior a su fecha de inicio de labores: aún no laboraba — no es
      // ausencia ni día laborable, no ensucia la métrica. (Comparación de strings
      // YYYY-MM-DD; sin fecha en el expediente, todo sigue como siempre.)
      if (emp.inicioLabores && f < emp.inicioLabores) {
        detalleDias.push({ fecha: f, cat: 'SIN_INGRESO', revisionPendientes: 0 });
        return;
      }
      var marcasF = marcasPorEmailFecha[emailMin + '|' + f];
      var revisionPendientes = pendientesDe(marcasF);
      resumen.revisionPendientes += revisionPendientes;
      // Horario del colaborador VIGENTE ese día: si tiene versiones por mes, un día
      // de julio se mide contra el horario de julio aunque hoy tenga otro distinto.
      // Horario especial de ESTA fecha: reemplaza las horas del horario base y
      // anula las excepciones porDia (esa fecha se mide contra el especial).
      // Las tolerancias y días laborales siguen siendo los del colaborador.
      var horarioBaseDia = horarioVigente(emp.horarios, emp.horario, f);
      // Sin horario vigente ESE día (ej. su horario "sin hora fija" arranca después, 23/10/2026): el día no se mide
      // (no es ausencia ni día laborable), igual que antes de su inicio de labores.
      if (!esHorarioValido(horarioBaseDia)) {
        detalleDias.push({ fecha: f, cat: 'SIN_HORARIO', revisionPendientes: revisionPendientes });
        return;
      }
      // Un horario especial no le aplica a quien no tiene hora fija: uno "para todos" le pondría hora de entrada.
      var espH = esSinHoraFija(horarioBaseDia) ? null : (horEspDe(horEspPorEmail[emailMin], f) || horEspDe(horEspGlobal, f));
      var horarioDia = espH ? Object.assign({}, horarioBaseDia, espH, { porDia: undefined }) : horarioBaseDia;
      var permisoHorasDia = (permHorasPorUid[emp.usuarioId] || {})[f];
      var c = clasificar(
        f, horarioDia,
        marcasF,
        feriados.has(f) || !!(espEmp && espEmp.has(f)),
        enRango(vacPorUid[emp.usuarioId], f),
        (permPorUid[emp.usuarioId] && permPorUid[emp.usuarioId].has(f)),
        enRango(incPorEmail[emailMin], f),
        enRango(omiPorEmail['*'], f) || enRango(omiPorEmail[emailMin], f),
        permisoHorasDia != null ? permisoHorasDia : null,
        enRango(hoPorUid[emp.usuarioId], f)
      );
      c.revisionPendientes = revisionPendientes;
      // Nota para la UI/Excel: ese día se midió contra un horario especial
      if (espH) c.horarioEspecial = { entrada: espH.entrada, salida: espH.salida };
      if (c.cat === 'A_TIEMPO' || c.cat === 'TARDE') {
        resumen.presentes++;
        if (c.cat === 'TARDE') { resumen.tardanzas++; resumen.tardanzaMin += c.tardanzaMin || 0; }
        if (c.estadoAlmuerzo === 'EXCEDIDO') { resumen.almuerzosExcedidos++; resumen.almuerzoExcesoMin += c.almuerzoExcesoMin || 0; }
        if (c.salidaTempranaMin) { resumen.salidasTempranas++; resumen.salidaTempranaMin += c.salidaTempranaMin; }
        if (c.trabajadoMin) resumen.trabajadoMin += c.trabajadoMin;
      } else if (c.cat === 'JORNADA') {
        // Sin hora fija: es un día presente (la presencia y la Evaluación lo cuentan), pero NO suma a "Horas laboradas"
        // ni a la puntualidad. Los contadores propios se crean solo si hay jornadas: el resumen de los demás no cambia.
        resumen.presentes++;
        resumen.jornadas = (resumen.jornadas || 0) + 1;
        if (c.jornadaAbierta) resumen.jornadasSinFin = (resumen.jornadasSinFin || 0) + 1;
        else {
          resumen.jornadasCerradas = (resumen.jornadasCerradas || 0) + 1;
          resumen.duracionJornadaMin = (resumen.duracionJornadaMin || 0) + (c.duracionMin || 0);
        }
      } else if (c.cat === 'HOME_OFFICE') { resumen.homeOffice++; if (c.trabajadoMin) resumen.trabajadoMin += c.trabajadoMin; }
      else if (c.cat === 'AUSENTE') resumen.ausentes++;
      else if (c.cat === 'VACACIONES' || c.cat === 'PERMISO' || c.cat === 'INCAPACIDAD' || c.cat === 'OMITIDO') resumen.justificadas++;
      else if (c.cat === 'FERIADO') resumen.feriados++;
      if (c.cat !== 'DESCANSO') resumen.diasLaborables++;
      detalleDias.push(Object.assign({ fecha: f }, c));
    });
    return Object.assign({}, emp, { deptoNom: deptoNombre[emp.deptoId] || '—', resumen: resumen, detalleDias: detalleDias });
  }).sort(function (a, b) { return a.nombre.localeCompare(b.nombre, 'es'); });

  return { filas: filas, diasRango: diasArr.length, trackedCount: tracked.length };
}

module.exports = {
  pad: pad,
  isoDe: isoDe,
  ddmmyyyy: ddmmyyyy,
  parseISO: parseISO,
  dow: dow,
  minutos: minutos,
  DIAS_NOMBRE: DIAS_NOMBRE,
  hora12: hora12,
  fmtMin: fmtMin,
  fmtDur: fmtDur,
  rangoDias: rangoDias,
  horarioDelDia: horarioDelDia,
  horarioVigente: horarioVigente,
  esHorarioValido: esHorarioValido,
  esSinHoraFija: esSinHoraFija,
  calcularHorasPermiso: calcularHorasPermiso,
  clasificar: clasificar,
  colaboradoresConHorario: colaboradoresConHorario,
  armarAsistencia: armarAsistencia
};
